import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { soundManager } from '../audio/soundManager';
import { gameLogger, type LogCategory } from '../core/gameLogger';
import type { Game } from '../core/game';
import { COLORS, FONTS, textStyle, drawPanel, drawBeveledButton, BUTTON_THEMES } from './theme';

export interface GameSettings {
  masterVolume: number;
  voiceVolume: number;
  soundEnabled: boolean;
  showDamageNumbers: boolean;
  cameraShake: boolean;
}

const TOGGLE_ON = COLORS.go;
const TOGGLE_OFF = 0x33446b;

function labelStyle(size: number, fill: number | string = COLORS.text): TextStyle {
  return new TextStyle({ fontFamily: FONTS.ui, fontSize: size, fontWeight: '600', fill });
}

export class SettingsMenu {
  container: Container;
  private background: Graphics;
  private menuBackground: Graphics;
  private titleText: Text;
  private visible: boolean = false;
  private settings: GameSettings;
  private sliders: Map<
    string,
    { bg: Graphics; fill: Graphics; handle: Graphics; text: Text; value: number }
  > = new Map();
  private toggles: Map<string, { bg: Graphics; handle: Graphics }> = new Map();
  private onClose?: () => void;
  private game: Game | null = null;
  private isMobile = false;
  private menuHalfHeight = 350;
  /** Half-width available for content (labels/sliders/toggles) inside the panel. */
  private contentInner = 200;

  constructor(
    width: number,
    height: number,
    initialSettings: GameSettings,
    game: Game | null,
    onClose?: () => void,
  ) {
    this.settings = { ...initialSettings };
    this.game = game;
    this.onClose = onClose;
    this.container = new Container();
    this.container.visible = false;
    this.container.eventMode = 'static';

    this.background = new Graphics();
    this.background.rect(0, 0, width, height);
    this.background.fill({ color: 0x06101f, alpha: 0.72 });
    this.container.addChild(this.background);

    const isMobile = height < 800;
    const menuHeight = isMobile ? Math.min(height - 40, 600) : 700;
    const menuHalfHeight = menuHeight / 2;
    // Clamp the panel to the viewport so content never spills off a phone.
    const panelHalfW = Math.min(250, Math.floor(width / 2 - 14));
    this.isMobile = isMobile;
    this.menuHalfHeight = menuHalfHeight;
    this.contentInner = Math.min(200, panelHalfW - 26);

    this.menuBackground = new Graphics();
    drawPanel(this.menuBackground, -panelHalfW, -menuHalfHeight, panelHalfW * 2, menuHeight, {
      radius: 18,
      accent: COLORS.gold,
    });
    this.menuBackground.position.set(width / 2, height / 2);
    this.container.addChild(this.menuBackground);

    this.titleText = new Text({
      text: 'SETTINGS',
      style: textStyle({ size: isMobile ? 30 : 38, display: true, color: COLORS.gold, outline: 4 }),
    });
    this.titleText.anchor.set(0.5, 0.5);
    this.titleText.position.set(width / 2, height / 2 - menuHalfHeight + 40);
    this.container.addChild(this.titleText);

    this.createSettings(width / 2, height / 2);
    this.createButtons(width / 2, height / 2);
  }

  private createSettings(centerX: number, centerY: number): void {
    const startY = centerY - 220;
    const spacing = 55;

    this.createSlider(
      'Master Volume',
      centerX,
      startY,
      this.settings.masterVolume * 100,
      'masterVolume',
    );
    this.createSlider(
      'Voice Volume',
      centerX,
      startY + spacing,
      this.settings.voiceVolume * 100,
      'voiceVolume',
    );

    this.createToggle(
      'Sound Enabled',
      centerX,
      startY + spacing * 2,
      this.settings.soundEnabled,
      'soundEnabled',
    );
    this.createToggle(
      'Damage Numbers',
      centerX,
      startY + spacing * 3,
      this.settings.showDamageNumbers,
      'showDamageNumbers',
    );
    this.createToggle(
      'Camera Shake',
      centerX,
      startY + spacing * 4,
      this.settings.cameraShake,
      'cameraShake',
    );

    const isMultiplayer = this.game && (this.game as any).networkManager !== null;
    if (isMultiplayer) {
      const showNetworkOSD = (this.game && (this.game as any).uiSettings?.showNetworkOSD) || false;
      this.createToggle(
        'Network Debug OSD',
        centerX,
        startY + spacing * 5,
        showNetworkOSD,
        'showNetworkOSD',
      );
    }

    // Console-logging toggles are a desktop dev aid (no console on mobile);
    // skipping them on phones also keeps the panel uncluttered and clear of
    // the bottom button.
    if (!this.isMobile) {
      this.createDebugLoggingSection(centerX, startY + spacing * 6.2);
    }
  }

  private createDebugLoggingSection(centerX: number, startY: number): void {
    const sectionLabel = new Text({
      text: '— Debug Logging —',
      style: labelStyle(14, COLORS.textDim),
    });
    sectionLabel.anchor.set(0.5, 0.5);
    sectionLabel.position.set(centerX, startY - 10);
    this.container.addChild(sectionLabel);

    const logSettings = gameLogger.getSettings();
    const spacing = 40;

    this.createSmallToggle(
      'Enable Logs',
      centerX - 100,
      startY + spacing * 0.5,
      logSettings.enabled,
      'log_enabled',
    );

    const categories: LogCategory[] = ['Turn', 'Weapon', 'Damage', 'Network', 'Entity', 'Game'];
    const col1 = categories.slice(0, 3);
    const col2 = categories.slice(3);

    col1.forEach((cat, i) => {
      this.createSmallToggle(
        cat,
        centerX - 100,
        startY + spacing * (i + 1.5),
        logSettings.categories[cat],
        `log_${cat}`,
      );
    });

    col2.forEach((cat, i) => {
      this.createSmallToggle(
        cat,
        centerX + 60,
        startY + spacing * (i + 1.5),
        logSettings.categories[cat],
        `log_${cat}`,
      );
    });
  }

  private createSmallToggle(
    label: string,
    x: number,
    y: number,
    initialValue: boolean,
    key: string,
  ): void {
    const labelText = new Text({ text: label, style: labelStyle(14, COLORS.textDim) });
    labelText.anchor.set(0, 0.5);
    labelText.position.set(x - 60, y);
    this.container.addChild(labelText);

    const toggleBg = new Graphics();
    this.drawToggleBg(toggleBg, 40, 20, initialValue);
    toggleBg.position.set(x + 50, y - 10);
    toggleBg.eventMode = 'static';
    toggleBg.cursor = 'pointer';
    this.container.addChild(toggleBg);

    const toggleHandle = new Graphics();
    this.drawToggleHandle(toggleHandle, 8);
    toggleHandle.position.set(x + 50 + (initialValue ? 30 : 10), y);
    this.container.addChild(toggleHandle);

    this.toggles.set(key, { bg: toggleBg, handle: toggleHandle });

    toggleBg.on('pointerdown', () => {
      const toggle = this.toggles.get(key);
      if (!toggle) return;

      const currentValue = toggle.handle.position.x > x + 50 + 20;
      const newValue = !currentValue;

      this.drawToggleBg(toggle.bg, 40, 20, newValue);
      toggle.bg.position.set(x + 50, y - 10);
      toggle.handle.position.x = x + 50 + (newValue ? 30 : 10);

      this.updateSetting(key, newValue);
    });
  }

  private drawToggleBg(g: Graphics, w: number, h: number, on: boolean): void {
    g.clear();
    g.roundRect(0, 0, w, h, h / 2).fill({ color: on ? TOGGLE_ON : TOGGLE_OFF });
    g.roundRect(0, 0, w, h, h / 2).stroke({
      width: 1.5,
      color: on ? COLORS.goDeep : COLORS.stroke,
    });
  }

  private drawToggleHandle(g: Graphics, r: number): void {
    g.circle(0, 0, r).fill({ color: COLORS.white });
    g.circle(0, -1, r - 2).fill({ color: 0xffffff, alpha: 0.5 });
  }

  private createSlider(
    label: string,
    x: number,
    y: number,
    initialValue: number,
    key: string,
  ): void {
    const inner = this.contentInner;
    const trackW = inner;
    const left = x - inner;

    const labelText = new Text({ text: label, style: labelStyle(18) });
    labelText.anchor.set(0, 0.5);
    labelText.position.set(left, y - 20);
    this.container.addChild(labelText);

    const sliderBg = new Graphics();
    sliderBg.roundRect(0, 0, trackW, 8, 4).fill({ color: 0x05101f });
    sliderBg.roundRect(0, 0, trackW, 8, 4).stroke({ width: 1, color: COLORS.stroke });
    sliderBg.position.set(left, y + 5);
    this.container.addChild(sliderBg);

    const sliderFill = new Graphics();
    this.drawSliderFill(sliderFill, (initialValue / 100) * trackW);
    sliderFill.position.set(left, y + 5);
    this.container.addChild(sliderFill);

    const handle = new Graphics();
    this.drawSliderHandle(handle);
    handle.position.set(left + (initialValue / 100) * trackW, y + 9);
    handle.eventMode = 'static';
    handle.cursor = 'pointer';
    this.container.addChild(handle);

    const valueText = new Text({
      text: `${Math.round(initialValue)}%`,
      style: labelStyle(16, COLORS.gold),
    });
    valueText.anchor.set(0, 0.5);
    valueText.position.set(left + trackW + 14, y);
    this.container.addChild(valueText);

    let dragging = false;

    handle.on('pointerdown', () => {
      dragging = true;
    });

    this.container.on('pointermove', (e) => {
      if (!dragging) return;

      const localX = e.global.x - left;
      const clampedX = Math.max(0, Math.min(trackW, localX));
      const value = (clampedX / trackW) * 100;

      handle.position.x = left + clampedX;
      this.drawSliderFill(sliderFill, clampedX);
      valueText.text = `${Math.round(value)}%`;

      this.updateSetting(key, value / 100);
    });

    this.container.on('pointerup', () => {
      dragging = false;
    });

    this.container.on('pointerupoutside', () => {
      dragging = false;
    });

    this.sliders.set(key, {
      bg: sliderBg,
      fill: sliderFill,
      handle,
      text: valueText,
      value: initialValue,
    });
  }

  private drawSliderFill(g: Graphics, w: number): void {
    g.clear();
    if (w > 0) g.roundRect(0, 0, w, 8, 4).fill({ color: COLORS.gold });
  }

  private drawSliderHandle(g: Graphics): void {
    g.circle(0, 0, 12).fill({ color: COLORS.white });
    g.circle(0, -2, 9).fill({ color: 0xffffff, alpha: 0.5 });
    g.circle(0, 0, 12).stroke({ width: 2, color: COLORS.goldDeep });
  }

  private createToggle(
    label: string,
    x: number,
    y: number,
    initialValue: boolean,
    key: string,
  ): void {
    const left = x - this.contentInner;
    const toggleX = x + this.contentInner - 60;

    const labelText = new Text({ text: label, style: labelStyle(18) });
    labelText.anchor.set(0, 0.5);
    labelText.position.set(left, y);
    this.container.addChild(labelText);

    const toggleBg = new Graphics();
    this.drawToggleBg(toggleBg, 60, 30, initialValue);
    toggleBg.position.set(toggleX, y - 15);
    toggleBg.eventMode = 'static';
    toggleBg.cursor = 'pointer';
    this.container.addChild(toggleBg);

    const toggleHandle = new Graphics();
    this.drawToggleHandle(toggleHandle, 12);
    toggleHandle.position.set(toggleX + (initialValue ? 45 : 15), y);
    this.container.addChild(toggleHandle);

    this.toggles.set(key, { bg: toggleBg, handle: toggleHandle });

    toggleBg.on('pointerdown', () => {
      let currentValue: boolean;
      if (key === 'soundEnabled') {
        currentValue = this.settings.soundEnabled;
      } else if (key === 'showDamageNumbers') {
        currentValue = this.settings.showDamageNumbers;
      } else if (key === 'cameraShake') {
        currentValue = this.settings.cameraShake;
      } else if (key === 'showNetworkOSD') {
        currentValue = (this.game && (this.game as any).uiSettings?.showNetworkOSD) || false;
      } else {
        currentValue = false;
      }
      const newValue = !currentValue;

      this.drawToggleBg(toggleBg, 60, 30, newValue);
      toggleBg.position.set(toggleX, y - 15);
      toggleHandle.position.x = toggleX + (newValue ? 45 : 15);

      this.updateSetting(key, newValue);
    });
  }

  private updateSetting(key: string, value: number | boolean): void {
    if (key === 'masterVolume' && typeof value === 'number') {
      this.settings.masterVolume = value;
      soundManager.setMasterVolume(value);
    } else if (key === 'voiceVolume' && typeof value === 'number') {
      this.settings.voiceVolume = value;
      soundManager.setVoiceVolume(value);
    } else if (key === 'soundEnabled' && typeof value === 'boolean') {
      this.settings.soundEnabled = value;
      soundManager.setEnabled(value);
    } else if (key === 'showDamageNumbers' && typeof value === 'boolean') {
      this.settings.showDamageNumbers = value;
      if (this.game) {
        this.game.updateUISettings({ showDamageNumbers: value });
      }
    } else if (key === 'cameraShake' && typeof value === 'boolean') {
      this.settings.cameraShake = value;
      if (this.game) {
        this.game.updateUISettings({ cameraShake: value });
      }
    } else if (key === 'showNetworkOSD' && typeof value === 'boolean') {
      if (this.game) {
        this.game.updateUISettings({ showNetworkOSD: value });
      }
    } else if (key === 'log_enabled' && typeof value === 'boolean') {
      if (value) {
        gameLogger.enable();
      } else {
        gameLogger.disable();
      }
    } else if (key.startsWith('log_') && typeof value === 'boolean') {
      const category = key.substring(4) as LogCategory;
      if (value) {
        gameLogger.enableCategory(category);
      } else {
        gameLogger.disableCategory(category);
      }
    }
  }

  private createButtons(centerX: number, centerY: number): void {
    // Pin to the bottom of the panel so it never collides with the content.
    const buttonY = centerY + this.menuHalfHeight - 40;
    this.createButton('Apply & Close', centerX, buttonY, () => {
      this.hide();
      if (this.onClose) {
        this.onClose();
      }
    });
  }

  private createButton(label: string, x: number, y: number, onClick: () => void): void {
    const button = new Graphics();
    drawBeveledButton(button, -150, -22, 300, 44, BUTTON_THEMES.green, 12);
    button.position.set(x, y);
    button.eventMode = 'static';
    button.cursor = 'pointer';
    button.on('pointerdown', onClick);
    button.on('pointerover', () => {
      button.tint = 0xdddddd;
    });
    button.on('pointerout', () => {
      button.tint = 0xffffff;
    });

    const text = new Text({ text: label, style: textStyle({ size: 20, weight: '700' }) });
    text.anchor.set(0.5, 0.5);
    text.position.set(x, y);

    this.container.addChild(button);
    this.container.addChild(text);
  }

  show(): void {
    this.visible = true;
    this.container.visible = true;
  }

  hide(): void {
    this.visible = false;
    this.container.visible = false;
  }

  isVisible(): boolean {
    return this.visible;
  }

  getSettings(): GameSettings {
    return { ...this.settings };
  }

  resize(width: number, height: number): void {
    this.background.clear();
    this.background.rect(0, 0, width, height);
    this.background.fill({ color: 0x06101f, alpha: 0.72 });

    this.menuBackground.position.set(width / 2, height / 2);
    this.titleText.position.set(width / 2, height / 2 - 250);
  }

  destroy(): void {
    this.container.destroy({ children: true });
  }
}
